import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import { useCreateBusinessMutation } from "../../store/api";

const initialState = {
  businessCode: "",
  businessName: "",
  legalName: "",
  timeZone: "America/New_York",
  currencyCode: "USD",
  contactEmail: "",
  contactPhone: "",
  businessAdmin: {
    email: "",
    displayName: "",
    password: "",
  },
};

const normalizeBusinessCode = (value) => value.toUpperCase().replace(/\s+/g, "");
const normalizeSlug = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const CreateBusiness = () => {
  const [form, setForm] = useState(initialState);
  const [createdBusinessId, setCreatedBusinessId] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [createBusiness, { isLoading, isError, error }] = useCreateBusinessMutation();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.key]);

  useEffect(() => {
    const restoredState = location.state?.restoreCreatedBusiness;
    if (restoredState?.businessId && restoredState?.formSnapshot) {
      setForm(restoredState.formSnapshot);
      setCreatedBusinessId(restoredState.businessId);
      setIsViewOnly(true);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    }
  }, [location.state]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name.startsWith("businessAdmin.")) {
      const field = name.replace("businessAdmin.", "");
      setForm((prev) => ({
        ...prev,
        businessAdmin: { ...prev.businessAdmin, [field]: value },
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isViewOnly) {
      return;
    }

    setIsViewOnly(false);
    setCreatedBusinessId(null);
    const businessCode = normalizeBusinessCode(form.businessCode);
    const businessName = form.businessName?.trim() || "Business";
    const legalName = form.legalName?.trim() || businessName;
    const fallbackSlugBase = businessCode || businessName;
    const storeSlug = normalizeSlug(fallbackSlugBase) || "store-main";

    const payload = {
      ...form,
      businessCode,
      // Backend currently requires Restaurant and Store objects during business creation.
      restaurant: {
        restaurantName: legalName,
      },
      store: {
        storeCode: businessCode ? `${businessCode}-MAIN` : "STORE-MAIN",
        storeSlug,
        storeName: `${businessName} Main Store`,
        countryCode: "US",
      },
    };

    try {
      const result = await createBusiness(payload).unwrap();
      const businessId = result?.businessId || result?.id;
      if (businessId) {
        setCreatedBusinessId(businessId);
        setIsViewOnly(true);
      }
    } catch (err) {
      // error handled by UI
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create Business"
        subtitle="Onboard a tenant with business details and business admin."
      />

      <Card>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <Section title="Business Details">
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Business Code"
                name="businessCode"
                placeholder="NOVAFOOD"
                helper="Uppercase letters only."
                value={form.businessCode}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Business Name"
                name="businessName"
                placeholder="Nova Food Group"
                value={form.businessName}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Legal Name"
                name="legalName"
                placeholder="Nova Food Group LLC"
                value={form.legalName}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Time Zone"
                name="timeZone"
                placeholder="America/New_York"
                value={form.timeZone}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Currency Code"
                name="currencyCode"
                placeholder="USD"
                value={form.currencyCode}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Contact Email"
                name="contactEmail"
                placeholder="ops@company.com"
                value={form.contactEmail}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Contact Phone"
                name="contactPhone"
                placeholder="+1-555-222-1111"
                value={form.contactPhone}
                onChange={handleChange}
                disabled={isViewOnly}
              />
            </div>
          </Section>

          <Section title="Business Admin">
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Business Admin Email"
                name="businessAdmin.email"
                placeholder="admin@company.com"
                value={form.businessAdmin.email}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Business Admin Name"
                name="businessAdmin.displayName"
                placeholder="Nova Admin"
                value={form.businessAdmin.displayName}
                onChange={handleChange}
                disabled={isViewOnly}
              />
              <Input
                label="Business Admin Password"
                name="businessAdmin.password"
                type="password"
                placeholder="BizAdmin@123"
                value={form.businessAdmin.password}
                onChange={handleChange}
                disabled={isViewOnly}
              />
            </div>
          </Section>

          {createdBusinessId && (
            <Alert tone="success">
              {isViewOnly
                ? "Business already created. You can continue adding stores."
                : "Business created successfully. You can now add a store."}
            </Alert>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to create business."}
            </Alert>
          )}
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="md:basis-[70%]">
              <Button type="submit" className="w-full" disabled={isLoading || isViewOnly}>
                {isViewOnly ? "Business Created" : isLoading ? "Creating..." : "Create Business"}
              </Button>
            </div>
            <div className="md:basis-[30%]">
              <Link
                to={`/businesses/${createdBusinessId || "new"}/stores/new`}
                state={
                  createdBusinessId
                    ? {
                        createBusinessContext: {
                          businessId: createdBusinessId,
                          formSnapshot: form,
                        },
                      }
                    : undefined
                }
              >
                <Button
                  type="button"
                  className="w-full"
                  variant={createdBusinessId ? "primary" : "secondary"}
                >
                  Add Store
                </Button>
              </Link>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateBusiness;

